/**
 * Displays remaining lives as small heart icons in the HUD.
 * Always shows INITIAL_LIVES icons; lost lives are dimmed.
 *
 * Active hearts periodically play a heartbeat pulse animation.
 * When the player collects food, a special heartbeat with a soft
 * green glow is triggered via playCollectEffect().
 * When a life is gained (e.g. coin milestone), a fill glow effect
 * with a green-border pulse is triggered via playLifeGainedEffect().
 */

import { TEX_LIFE_ICON } from '../utils/constants.js';
import { INITIAL_LIVES } from '../config/game-config.js';

/** Base display scale for the 20px heart texture (≈14px on screen). */
const ICON_BASE_SCALE = 0.7;

/** Spacing between heart icons in pixels. */
const ICON_SPACING = 18;

export default class LivesDisplay {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x - Starting x position.
   * @param {number} y - Y position.
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    /** Current number of active lives. */
    this._currentLives = INITIAL_LIVES;

    /** @type {Phaser.GameObjects.Sprite[]} */
    this.icons = [];

    /** @type {Phaser.GameObjects.Graphics[]} Green glow graphics per icon. */
    this._glows = [];

    /** @type {Phaser.GameObjects.Graphics[]} Red glow graphics per icon (created on demand). */
    this._redGlows = [];

    this._createIcons(INITIAL_LIVES);
    this._startHeartbeatLoop();
  }

  /**
   * Update the displayed number of lives.
   * Icons for active lives are fully opaque; lost lives are dimmed.
   *
   * @param {number} lives
   */
  setLives(lives) {
    this._currentLives = lives;
    for (let i = 0; i < this.icons.length; i++) {
      this.icons[i].setAlpha(i < lives ? 1 : 0.2);
      // Reset scale on dimmed hearts (in case a pulse was mid-tween)
      if (i >= lives) {
        this.icons[i].setScale(ICON_BASE_SCALE);
      }
    }
  }

  /**
   * Play a life-lost animation on the heart that was just lost.
   * Sequence: red glow pulse + drain (scaleY shrink) → dim to 0.2 alpha.
   *
   * @param {number} lives - The NEW lives count (post-decrement).
   *   The lost heart index is `lives` (0-indexed).
   */
  playLifeLostEffect(lives) {
    const lostIndex = lives; // icons[lives] is the heart that was just lost
    const icon = this.icons[lostIndex];
    if (!icon) {
      // Fallback: just set lives immediately
      this.setLives(lives);
      return;
    }

    // Update _currentLives IMMEDIATELY so the periodic heartbeat and
    // collect effect no longer target this heart while it's animating.
    this._currentLives = lives;

    // Kill any in-flight tweens on this icon (heartbeat / collect pulse)
    // to prevent scale conflicts that leave the heart distorted.
    this.scene.tweens.killTweensOf(icon);

    // Ensure the icon starts from a clean scale state
    icon.setScale(ICON_BASE_SCALE, ICON_BASE_SCALE);

    // Create red glow if not already created for this index
    if (!this._redGlows[lostIndex]) {
      const redGlow = this.scene.add.graphics();
      redGlow.setAlpha(0);
      redGlow.fillStyle(0xcc4444, 0.45);
      redGlow.fillCircle(0, 0, 10);
      redGlow.fillStyle(0xcc4444, 0.2);
      redGlow.fillCircle(0, 0, 14);
      redGlow.setPosition(
        icon.x + (10 * ICON_BASE_SCALE),
        icon.y,
      );
      this._redGlows[lostIndex] = redGlow;
    }

    const redGlow = this._redGlows[lostIndex];

    // 1) Red glow pulse: fade in → hold → fade out (~400ms total)
    this.scene.tweens.add({
      targets: redGlow,
      alpha: 1,
      duration: 150,
      yoyo: true,
      hold: 100,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (redGlow) redGlow.setAlpha(0);
      },
    });

    // Store the original x position before any shake tween
    const originalX = this.x + lostIndex * ICON_SPACING;

    // 2) Drain effect: scaleY shrinks from base → 0 (emptying top-to-bottom)
    this.scene.tweens.add({
      targets: icon,
      scaleY: 0,
      duration: 350,
      delay: 50,
      ease: 'Quad.easeIn',
      onComplete: () => {
        if (!icon || !icon.scene) return;
        // Restore to clean state: uniform scale, dimmed, exact position
        icon.setScale(ICON_BASE_SCALE, ICON_BASE_SCALE);
        icon.setAlpha(0.2);
        icon.setX(originalX);
      },
    });

    // 3) Small horizontal shake during drain for emphasis
    this.scene.tweens.add({
      targets: icon,
      x: originalX + 2,
      duration: 40,
      yoyo: true,
      repeat: 3,
      delay: 50,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (icon && icon.scene) {
          icon.setX(originalX);
        }
      },
    });
  }

  /**
   * Play a life-gained animation on the heart that was just recovered.
   * Sequence: instant show → bright fill glow expanding inward →
   * green-border pulse (like food collect) → settle at full opacity.
   *
   * @param {number} lives - The NEW lives count (post-increment).
   *   The gained heart index is `lives - 1`.
   */
  playLifeGainedEffect(lives) {
    const gainedIndex = lives - 1;
    const icon = this.icons[gainedIndex];
    if (!icon) {
      this.setLives(lives);
      return;
    }

    this._currentLives = lives;

    // Kill any in-flight tweens on this icon
    this.scene.tweens.killTweensOf(icon);

    // Start invisible and small, then grow in with a glow
    icon.setAlpha(0);
    icon.setScale(0);

    // Phase 1: Scale up from 0 to slightly larger than normal with a flash
    this.scene.tweens.add({
      targets: icon,
      scaleX: ICON_BASE_SCALE * 1.5,
      scaleY: ICON_BASE_SCALE * 1.5,
      alpha: 1,
      duration: 250,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!icon || !icon.scene) return;

        // Phase 2: Settle to normal base scale
        this.scene.tweens.add({
          targets: icon,
          scaleX: ICON_BASE_SCALE,
          scaleY: ICON_BASE_SCALE,
          duration: 150,
          ease: 'Quad.easeOut',
          onComplete: () => {
            if (!icon || !icon.scene) return;

            // Phase 3: Double-pulse heartbeat (like food collect)
            this.scene.tweens.add({
              targets: icon,
              scaleX: ICON_BASE_SCALE * 1.3,
              scaleY: ICON_BASE_SCALE * 1.3,
              duration: 100,
              yoyo: true,
              ease: 'Quad.easeOut',
              onComplete: () => {
                if (!icon || !icon.scene) return;
                this.scene.tweens.add({
                  targets: icon,
                  scaleX: ICON_BASE_SCALE * 1.15,
                  scaleY: ICON_BASE_SCALE * 1.15,
                  duration: 80,
                  yoyo: true,
                  ease: 'Quad.easeOut',
                });
              },
            });
          },
        });
      },
    });

    // Green glow pulse (same as food collect, but slightly stronger)
    const glow = this._glows[gainedIndex];
    if (glow) {
      this.scene.tweens.add({
        targets: glow,
        alpha: 1,
        duration: 300,
        yoyo: true,
        hold: 200,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          if (glow) glow.setAlpha(0);
        },
      });
    }
  }

  /** Clean up all icons, glows, and timers. */
  destroy() {
    if (this._heartbeatTimer) {
      this._heartbeatTimer.destroy();
      this._heartbeatTimer = null;
    }
    for (const glow of this._glows) {
      if (glow) glow.destroy();
    }
    this._glows = [];
    for (const glow of this._redGlows) {
      if (glow) glow.destroy();
    }
    this._redGlows = [];
    for (const icon of this.icons) {
      // Kill any active tweens on this icon before destroying
      this.scene.tweens.killTweensOf(icon);
      icon.destroy();
    }
    this.icons = [];
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /** @private */
  _createIcons(count) {
    for (let i = 0; i < count; i++) {
      const icon = this.scene.add.sprite(
        this.x + i * ICON_SPACING,
        this.y,
        TEX_LIFE_ICON,
      );
      icon.setOrigin(0, 0.5);
      icon.setScale(ICON_BASE_SCALE);
      this.icons.push(icon);

      // Pre-create a glow graphic for each heart (hidden by default)
      const glow = this.scene.add.graphics();
      glow.setAlpha(0);
      glow.fillStyle(0x44cc44, 0.35);
      glow.fillCircle(0, 0, 10);
      glow.fillStyle(0x44cc44, 0.15);
      glow.fillCircle(0, 0, 14);
      glow.setPosition(
        icon.x + (10 * ICON_BASE_SCALE),
        icon.y,
      );
      this._glows.push(glow);
    }
  }

  /**
   * Start a repeating timer that triggers the heartbeat animation
   * on all active hearts every few seconds.
   * @private
   */
  _startHeartbeatLoop() {
    // Heartbeat every 3-5 seconds (randomised per cycle)
    this._scheduleNextHeartbeat();
  }

  /**
   * Schedule the next heartbeat after a random delay.
   * @private
   */
  _scheduleNextHeartbeat() {
    const delay = 3000 + Math.random() * 2000;
    this._heartbeatTimer = this.scene.time.delayedCall(delay, () => {
      this._playHeartbeat();
      this._scheduleNextHeartbeat();
    });
  }

  /**
   * Play a double-pulse heartbeat animation on all active (non-dimmed) hearts.
   * This is the periodic idle animation — no green glow.
   * @private
   */
  _playHeartbeat() {
    for (let i = 0; i < this.icons.length; i++) {
      // Only animate active lives
      if (i >= this._currentLives) continue;

      const icon = this.icons[i];

      // Kill any lingering heartbeat tweens on this icon to prevent stacking
      this.scene.tweens.killTweensOf(icon);

      // Double-pulse: quick scale up → down → up → down (like a real heartbeat)
      this.scene.tweens.add({
        targets: icon,
        scaleX: ICON_BASE_SCALE * 1.3,
        scaleY: ICON_BASE_SCALE * 1.3,
        duration: 100,
        yoyo: true,
        ease: 'Quad.easeOut',
        onComplete: () => {
          // Second (smaller) beat
          this.scene.tweens.add({
            targets: icon,
            scaleX: ICON_BASE_SCALE * 1.15,
            scaleY: ICON_BASE_SCALE * 1.15,
            duration: 80,
            yoyo: true,
            ease: 'Quad.easeOut',
          });
        },
      });
    }
  }

  /**
   * Play a heartbeat with green glow effect on all active hearts.
   * Called when the player collects food.
   */
  playCollectEffect() {
    for (let i = 0; i < this.icons.length; i++) {
      if (i >= this._currentLives) continue;

      const icon = this.icons[i];

      // Double-pulse heartbeat
      this.scene.tweens.add({
        targets: icon,
        scaleX: ICON_BASE_SCALE * 1.3,
        scaleY: ICON_BASE_SCALE * 1.3,
        duration: 100,
        yoyo: true,
        ease: 'Quad.easeOut',
        onComplete: () => {
          this.scene.tweens.add({
            targets: icon,
            scaleX: ICON_BASE_SCALE * 1.15,
            scaleY: ICON_BASE_SCALE * 1.15,
            duration: 80,
            yoyo: true,
            ease: 'Quad.easeOut',
          });
        },
      });

      // Green glow (always shown on collect)
      const glow = this._glows[i];
      if (glow) {
        this.scene.tweens.add({
          targets: glow,
          alpha: 1,
          duration: 200,
          yoyo: true,
          hold: 100,
          ease: 'Sine.easeInOut',
          onComplete: () => {
            glow.setAlpha(0);
          },
        });
      }
    }
  }
}
